import { Component, type ReactNode } from "react";
import { Alert, Button, Stack, Text } from "@mantine/core";
import { captureException } from "../lib/observability";

type Props = { children: ReactNode };
type State = { error: Error | null };

/**
 * Safety net for the public apply flow. A candidate must never hit a blank
 * screen (e.g. an unexpected error while parsing a resume on a specific
 * device) — show a recoverable message and let them retry / fill manually.
 */
export class ApplyErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: unknown) {
    const componentStack =
      info && typeof info === "object" && "componentStack" in info
        ? String((info as { componentStack?: string }).componentStack || "").slice(0, 500)
        : undefined;
    captureException(error, {
      stage: "apply_error_boundary",
      has_component_stack: Boolean(componentStack),
    });
  }

  render() {
    if (this.state.error) {
      return (
        <Stack maw="var(--axio-unit-480)" mx="auto" mt="var(--axio-unit-80)" px="md">
          <Alert color="red" title="Something went wrong">
            <Text size="sm">
              We hit an unexpected error. Please reload and try again — you can
              skip the resume upload and fill your details manually.
            </Text>
          </Alert>
          <Button color="violet" onClick={() => window.location.reload()}>
            Reload and try again
          </Button>
        </Stack>
      );
    }
    return this.props.children;
  }
}
